import { useHubspotForm } from '@aaronhayes/react-use-hubspot-form'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { GetStaticProps, NextPage } from 'next'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'
import { AboutHeader } from 'src/components/AbouttHeader/AboutHeader'
import { Footer } from 'src/components/Footer/Footer'
import { Breadcrumb } from 'src/components/global/Breadcrumb'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { THEME } from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import { IAboutEntity } from 'src/graphql/generated/hooks'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'

type Props = {
  about: IAboutEntity
}

const About: NextPage<Props> = ({ about }: Props) => {
  const router = useRouter()
  const aboutInfo = about
  const type = router.query.type
  const [result, setResult] = useState({ visitorId: '', confidence: { score: 0 } })
  useEffect(() => {
    const loadFingerprint = async () => {
      const fp = await FingerprintJS.load()
      const result = await fp.get()
      // eslint-disable-next-line no-console
      console.log('FingerPrint data', result)
      setResult(result)
    }
    loadFingerprint()
  }, [])

  const [isFormLoaded, setIsFormLoaded] = useState({ desktop: false, mobile: false })
  const { loaded: isDesktopFormLoaded } = useHubspotForm({
    portalId: '21422664',
    formId: 'e6764df8-2bdd-451a-8614-84bfa06f71c9',
    target: '#my-hubspot-form',
  })
  const { loaded: isMobileFormLoaded } = useHubspotForm({
    portalId: '21422664',
    formId: 'e6764df8-2bdd-451a-8614-84bfa06f71c9',
    target: '#my-hubspot-form-mobile',
  })
  const [chunks, setChunks] = useState<number[][]>([])

  const chunkArray = useMemo(
    () =>
      (data: any[] | undefined, chunkSize: number): any[][] => {
        const result: any[][] = []
        if (data && data != undefined && data.length > 0) {
          for (let i = 0; i < data.length; i += chunkSize) {
            result.push(data.slice(i, i + chunkSize))
          }
        }
        return result
      },
    [],
  )

  const title = 'About US'
  const description =
    'Legaltech Hub is founded by a team of legal technology enthusiasts who in their career have acted as buyers, vendors, investors and advisors to buyers and vendors. With more than 50 years of collective experience in legaltech, the team is well placed to help bring transparency to the Legal Tech Market.\
  By listing all relevant legal tech solutions, Legal Tech Hub allows buyers of legal tech to see the woods from the trees, vendors to showcase their solutions and investors to understand the market. Industry insights complement the directory.'

  const meta = (
    <Meta
      title={title}
      description={description}
      url="/about/"
      keywords={['Nicola (Nikki) Shaver - CEO, Jeroen Plink - COO, Chris Ford - Advisor, Sam Whitman - Advisor']}
    />
  )

  const teamChunks = useMemo(() => chunkArray(about?.attributes?.ourTeam || [], 3), [about, chunkArray])

  useEffect(() => {
    setIsFormLoaded({
      desktop: isDesktopFormLoaded,
      mobile: isMobileFormLoaded,
    })
  }, [isDesktopFormLoaded, isMobileFormLoaded])

  useEffect(() => {
    setChunks(teamChunks)
    if (teamChunks && (isFormLoaded.desktop || isFormLoaded.mobile)) {
      setTimeout(() => {
        const mainObj = document.getElementById('mainContainer')
        if (type === undefined || type === 'who_we_are') {
          mainObj?.scrollTo({ top: 0 })
        } else {
          const sectionMap: { [key: string]: string } = {
            advisory_board: 'advisor-section',
            contact_us: 'contact-section',
            'm-advisory_board': 'm-advisor-section',
            'm-contact_us': 'm-contact-section',
          }
          const targetId = sectionMap[type as string]
          const obj = targetId ? document.getElementById(targetId) : null

          if (mainObj && obj) {
            mainObj.scrollTo({ top: obj.offsetTop })
          }
        }
      }, 800)
    }
  }, [teamChunks, isFormLoaded, type, router])

  return (
    <Main meta={meta}>
      <MainContainer id="mainContainer">
        <div>
          {`FingerPrint ID: ${result?.visitorId}`}
          <br></br>
          {`confidence score: ${result?.confidence?.score}`}
        </div>
        <div className="flex flex-col bg-clouds">
          <Breadcrumb subject="ABOUT US" />
          {aboutInfo && <AboutHeader about={aboutInfo} />}
        </div>
        <hr />

        <div className="w-full items-center justify-between sm:mb-[3rem]">
          <div className="flex flex-col pl-[1.25rem] pr-[1.25rem] !max-w-[1100px] w-full py-2 m-auto">
            <h2 className="pt-[0.1rem] pb-[0.125rem] md:text-[24px] text-[1.5rem] font-bold font-secondary text-[#222222] mb-[30px] md:mt-[20px] mt-[3rem] sm:mb-[3rem]">
              Our Leadership Team
            </h2>

            {chunks?.length > 0 &&
              chunks.map((teamList: any, index: number) => {
                return (
                  <div
                    key={index}
                    className="!max-w-[1100px] flex md:flex-row flex-col justify-between w-full sm:mb-1 gap-[50px]"
                  >
                    {teamList.map((team: any, ind: number) => {
                      return (
                        <div key={ind} className="flex flex-col md:w-1/2 w-full md:pr-[1.25rem] sm:mt-[1.2rem]">
                          {team?.externalLink ? (
                            <Link target={'_blank'} href={team?.externalLink}>
                              <div
                                className="w-[18.5rem] h-[17.5rem] px-3 large-lg:px-0 min-h-[15rem] flex justify-center md:justify-start bg-center md:bg-left"
                                style={{
                                  // backgroundImage: 'url(' + '/avatar-bg.png' + ')',
                                  width: '100%',
                                  backgroundSize: 'contain',
                                  backgroundRepeat: 'no-repeat',
                                }}
                              >
                                <Image
                                  src={team?.avatar?.data?.attributes?.url || ''}
                                  className="w-[240px] h-[240px] rounded-[120px]"
                                  width={240}
                                  height={240}
                                  priority
                                  placeholder="empty"
                                  alt=""
                                />
                              </div>
                              <h2 className="pt-[0.1rem] pb-[0.1rem] md:text-[1.4rem] text-[1.125rem] font-bold font-secondary text-[#222222] md:mt-[1rem]">
                                {team.name}
                              </h2>
                              <div className="md:text-[1.1rem] text-[1.125rem] font-secondary text-[#222222] mb-[0.1rem]">
                                {team.title}
                              </div>
                              <h3>
                                <HtmlContentViewer
                                  className="md:text-[1.125rem] text-[0.9375rem] font-primary font-regular text-darkgray"
                                  html={team.bio || ''}
                                />
                              </h3>
                            </Link>
                          ) : (
                            <>
                              <div
                                className="w-[18.5rem] h-[17.5rem] px-3 large-lg:px-0 min-h-[15rem] flex justify-center md:justify-start bg-center md:bg-left"
                                style={{
                                  // backgroundImage: 'url(' + '/avatar-bg.png' + ')',
                                  width: '100%',
                                  backgroundSize: 'contain',
                                  backgroundRepeat: 'no-repeat',
                                }}
                              >
                                <Image
                                  src={team?.avatar?.data?.attributes?.url || ''}
                                  className="w-[240px] h-[240px] rounded-[120px]"
                                  width={240}
                                  height={240}
                                  priority
                                  placeholder="empty"
                                  alt=""
                                />
                              </div>
                              <h2 className="pt-[0.1rem] pb-[0.1rem] md:text-[1.4rem] text-[1.125rem] font-bold font-secondary text-[#222222] md:mt-[1rem]">
                                {team.name}
                              </h2>
                              <div className="md:text-[1.1rem] text-[1.125rem] font-secondary text-[#222222] mb-[0.1rem]">
                                {team.title}
                              </div>
                              <h3>
                                <HtmlContentViewer
                                  className="md:text-[1.125rem] text-[0.9375rem] font-primary font-regular text-darkgray"
                                  html={team.bio || ''}
                                />
                              </h3>
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
          </div>
        </div>

        <div id="contact-section" className="flex flex-col bg-clouds">
          <div className="w-full items-center justify-between">
            <div className="flex flex-col pl-[1.25rem] pr-[1.25rem] !max-w-[1100px] w-full py-2 m-auto">
              <div className="pt-[0.1rem] pb-[0.125rem] text-[1.875rem] font-bold font-secondary text-[#222222] mt-[3rem]">
                Contact Us
              </div>

              <div className="flex flex-row w-full">
                <div className="flex flex-col w-full md:w-1/2">
                  <div id="my-hubspot-form"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ThemeOnly theme="legaltech">
          <Footer />
        </ThemeOnly>
      </MainContainer>
    </Main>
  )
}

export default About

export const getStaticProps: GetStaticProps = async ({}) => {
  if (THEME === 'ilta')
    return {
      revalidate: 60,
      notFound: true,
    }
  try {
    const { about } = await apolloSdk.getAbout()

    const aboutData = about?.data
    return {
      props: {
        about: aboutData,
      },
      revalidate: 60,
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error occurred in getStaticProps of About:', error)
    // Graceful fallback in case of an upstream error
    return { notFound: true, revalidate: 60 }
  }
}
